import * as React from "react";
import {
  useMenuContext_unstable,
  useArrowNavigationGroup,
} from "@fluentui/react-components";
import type { MenuItemShimProps } from "./MenuItemShim";
import {
  useDispatchOptimizationEnabled,
  useTriggerOnMenuDismissedOnSubmenuClose,
} from "../../hooks/useNextComponentsEnabled";
import { useCustomMenuNavigation } from "./useCustomMenuNavigation";

export const CustomMenuItem = (menuItemShimProps: MenuItemShimProps) => {
  // Using the useMenuContext_unstable hook to dismiss the menu and it's children when a custom rendered item is executed.
  const setOpen = useMenuContext_unstable((context) => context.setOpen);

  const isDispatchOptimizationEnabled = useDispatchOptimizationEnabled();
  const triggerOnMenuDismissedOnSubmenuClose =
    useTriggerOnMenuDismissedOnSubmenuClose();

  const totalColumns =
    menuItemShimProps.itemProps.data?.columns ||
    menuItemShimProps.itemProps.data?.columnCount ||
    1;

  /**
   * Currently we only have galleries or color cells as custom menu buttons.
   * If more types of custom buttons are added, this logic should be updated accordingly.
   */
  const customMenuButtons =
    menuItemShimProps.itemProps.data?.gallerySections?.[0]?.galleryButtons ||
    menuItemShimProps.itemProps.data?.colorCells ||
    [];
  const totalButtons = customMenuButtons.length;
  const rows = Math.ceil(totalButtons / totalColumns);

  const groupAttributes = useArrowNavigationGroup({
    axis: "grid-linear",
  });

  // Use custom navigation hook
  const { handleKeyDown } = useCustomMenuNavigation({
    totalColumns,
    totalButtons,
    rows,
  });

  const customMenuRef = React.useRef<HTMLDivElement>(null);

  if (isDispatchOptimizationEnabled || triggerOnMenuDismissedOnSubmenuClose) {
    return (
      <div
        ref={customMenuRef}
        role="menuitem"
        {...groupAttributes}
        data-grid-columns={totalColumns}
        onKeyDown={handleKeyDown}
        onClick={
          menuItemShimProps.onDismiss?.length === 0
            ? undefined
            : (e) => {
                setOpen(e, {
                  open: false,
                  keyboard: undefined,
                  bubble: true,
                  type: "menuItemClick",
                  event: e,
                });
              }
        }
      >
        {menuItemShimProps.itemProps.onRender?.(
          menuItemShimProps.itemProps,
          menuItemShimProps.onDismiss ? menuItemShimProps.onDismiss : () => {}
        )}
      </div>
    );
  }

  return menuItemShimProps.itemProps.onRender?.(
    menuItemShimProps.itemProps,
    menuItemShimProps.onDismiss ? menuItemShimProps.onDismiss : () => {}
  );
};
